import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { SortingState } from '@cast/design-system';
import { AllocationGroupWorkloadEfficiency } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const ALLOCATION_GROUPS_WORKLOADS_EFFICIENCY_QUERY_KEY =
  'allocation-groups-workloads-efficiency';

const getAllocationGroupWorkloadsEfficiencyQueryKey = ({
  groupId,
  startTime,
  endTime,
  useListingPrices,
  clusterIds,
  sortingState,
  pageLimit,
}: Partial<UseAllocationGroupWorkloadsEfficiencyQueryProps> = {}) =>
  [
    ALLOCATION_GROUPS_WORKLOADS_EFFICIENCY_QUERY_KEY,
    ...(groupId !== undefined
      ? [
          groupId,
          startTime,
          endTime,
          useListingPrices,
          clusterIds,
          sortingState,
          pageLimit,
        ]
      : []),
  ] as const;

type UseAllocationGroupWorkloadsEfficiencyQuery = {
  data?: AllocationGroupWorkloadEfficiency[];
  isLoading: boolean;
  error?: unknown;
  refetch?: () => void;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  isFetching: boolean;
  count: number;
};

type UseAllocationGroupWorkloadsEfficiencyQueryProps = {
  groupId: string;
  startTime: string;
  endTime: string;
  clusterIds: string[];
  useListingPrices: boolean;
  sortingState?: SortingState;
  pageLimit?: number;
};

const getAllocationGroupWorkloadsEfficiencyQueryOptions = ({
  groupId,
  startTime,
  endTime,
  clusterIds,
  useListingPrices,
  sortingState,
  pageLimit = 50,
}: UseAllocationGroupWorkloadsEfficiencyQueryProps) =>
  infiniteQueryOptions({
    queryKey: getAllocationGroupWorkloadsEfficiencyQueryKey({
      groupId,
      startTime,
      endTime,
      useListingPrices,
      clusterIds,
      sortingState,
      pageLimit,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } =
        await apiClient.costReport.getAllocationGroupWorkloadsEfficiency({
          groupId,
          clusterIds,
          startTime,
          endTime,
          useListingPrices,
          pageLimit: String(pageLimit),
          pageCursor: pageParam,
          sortField: sortingState?.columnId,
          sortOrder: sortingState?.sortBy.direction,
        });

      return data;
    },
    enabled: !!groupId,
    refetchOnWindowFocus: false,
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor || undefined;
    },
  });

export const useAllocationGroupWorkloadsEfficiencyQuery = (
  props: UseAllocationGroupWorkloadsEfficiencyQueryProps
): UseAllocationGroupWorkloadsEfficiencyQuery => {
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteQuery(
    getAllocationGroupWorkloadsEfficiencyQueryOptions(props)
  );

  return {
    isLoading,
    data: flatMap(data?.pages, (page) => page?.workloads ?? []),
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    count: Number(data?.pages[0].count ?? 0),
  };
};
